import axios, { AxiosError, AxiosResponse } from 'axios'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { GenericQueryOptions } from '.'
import {
  AcquisitionNumber,
  EditOrganizationUnit,
  EditUser,
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount,
  OrganizationUnit,
  OrganizationUnitGrid,
  OrganizationUnitPagedQuery,
  SetOrganizationUnitInsurerValid,
  UpdateOrganizationUnitDiscount,
  UserBase,
} from '../dtos'
import { InsuranceCategoryDiscount } from '../dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import { OrganizationUnitProperties } from '../dtos/OrganizationUnit/OrganizationUnitProperties'
import { TransferUser } from '../dtos/OrganizationUnit/TransferUser'
import { LdapUser } from '../dtos/User/LdapUser'
import { OrganizationUnitType, Product } from '../enums'

const baseUrl = process.env.PUBLIC_URL
const apiUrl = process.env.REACT_APP_ORGANIZATION_UNIT_API
const url = `${baseUrl}/${apiUrl}`

const fiveMinutes = 1000 * 60 * 5

export const useGetAll = () => {
  return useMutation(
    (search: OrganizationUnitPagedQuery) =>
      axios.post<OrganizationUnitGrid>(`${url}/GetAllOrganizationUnits`, search),
    {},
  )
}

export const useGetCurrentOrganizationUnit = () => {
  return useQuery<AxiosResponse<OrganizationUnit>, AxiosError>(
    ['organizationUnit', 'current'],
    () => axios.get<OrganizationUnit>(`${url}/GetCurrent`),
  )
}

export const useGetOrganizationUnitById = (ouId: number) => {
  return useQuery<AxiosResponse<OrganizationUnit>, AxiosError>(
    ['organizationUnit', ouId],
    () => axios.get<OrganizationUnit>(`${url}/GetById`, { params: { ouId } }),
    { staleTime: fiveMinutes },
  )
}

export const useGetInsurers = (ouId: number, product?: Product) => {
  return useQuery(
    ['organizationUnit', ouId, product, 'insurers'],
    () =>
      axios.get<InsurerOrganizationUnit[]>(`${url}/GetOrganizationUnitInsurers`, {
        params: { ouId, product },
      }),
    { staleTime: fiveMinutes },
  )
}

export const useGetAvailableInsurers = (ouId: number) => {
  return useQuery(
    ['organizationUnit', ouId, 'availableInsurers'],
    () =>
      axios.get<{ insurerId: number; insurer: string; ouId: number }[]>(
        `${url}/GetAvailableOrganizationUnitInsurers`,
        { params: { ouId } },
      ),
    { staleTime: fiveMinutes },
  )
}

export const useGetInsurerDiscounts = (
  ouId: number,
  insurerId: number,
  product: Product,
  options?: GenericQueryOptions<{
    assignedDiscounts: InsurerOrganizationUnitDiscount[]
    availableDiscounts: InsuranceCategoryDiscount[]
  }>,
) => {
  return useQuery(
    ['organizationUnit', ouId, product, 'insurers', insurerId],
    () =>
      axios.get<{
        assignedDiscounts: InsurerOrganizationUnitDiscount[]
        availableDiscounts: InsuranceCategoryDiscount[]
      }>(`${url}/GetInsurerDiscounts`, {
        params: { ouId, insurerId, product },
      }),
    options,
  )
}

export const useUpdateDiscount = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (updateDiscount: UpdateOrganizationUnitDiscount) =>
      axios.post(`${url}/UpdateDiscount`, updateDiscount),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([
          'organizationUnit',
          variables.organizationUnitId,
          variables.insuranceCategoryType,
        ])
      },
    },
  )
}

export const useSetInsurerValid = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (updateDiscount: SetOrganizationUnitInsurerValid) =>
      axios.post(`${url}/SetInsurerValid`, updateDiscount),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([
          'organizationUnit',
          variables.organizationUnitId,
          variables.insuranceCategoryType,
          'insurers',
        ])
      },
    },
  )
}

export const useGetOrganizationUnitProperties = (ouId: number) => {
  return useQuery(
    ['organizationUnit', ouId, 'properties'],
    () =>
      axios.get<OrganizationUnitProperties>(`${url}/GetOrganizationUnitProperties`, {
        params: { ouId },
      }),
    { staleTime: fiveMinutes },
  )
}

export const useUpdateOrganizationUnit = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (update: EditOrganizationUnit) => axios.post(`${url}/UpdateOrganizationUnit`, update),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['organizationUnit', variables.id])
      },
    },
  )
}

export const useCreateOrganizationUnit = () => {
  return useMutation(
    (update: EditOrganizationUnit) =>
      axios.post<OrganizationUnit>(`${url}/CreateOrganizationUnit`, update),
    {},
  )
}

export const useGetPossibleTypes = (
  ouId?: number | undefined,
  superiorOrganizationUnitId?: number | undefined,
) => {
  return useQuery(
    ['organizationUnit', ouId, 'possibleTypes', superiorOrganizationUnitId],
    () =>
      axios.get<OrganizationUnitType[]>(`${url}/GetPossibleOrganizationUnitTypes`, {
        params: { ouId, superiorOrganizationUnitId },
      }),
    { staleTime: fiveMinutes },
  )
}

export const useGetAcquisitionNumbers = (
  ouId?: number | undefined,
  superiorOrganizationUnitId?: number | undefined,
  options?: Omit<UseQueryOptions<AxiosResponse<AcquisitionNumber[]>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(
    ['organizationUnit', ouId, 'acquisitionNumbers', superiorOrganizationUnitId],
    () =>
      axios.get<AcquisitionNumber[]>(`${url}/GetAcquisitionNumbers`, {
        params: { ouId, superiorOrganizationUnitId },
      }),
    options,
  )
}

export const useCreateUser = () => {
  return useMutation((create: EditUser) => axios.post<UserBase>(`${url}/CreateUser`, create), {})
}

export const useTransferUser = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (transferUser: TransferUser) => axios.post(`${url}/TransferUser`, transferUser),
    { onSuccess: (_, variables) => queryClient.invalidateQueries(['user', variables.userId]) },
  )
}

export const useGetLdapUser = () => {
  return useMutation((userName: string) =>
    axios.get<LdapUser>(`${url}/GetLdapUser`, { params: { userName } }),
  )
}

export const useUploadLogo = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (form: { formData: FormData; ouId: number }) =>
      axios.post<string>(`${url}/UploadLogo`, form.formData, {
        params: { ouId: form.ouId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    {
      onSuccess: (_, variables) =>
        queryClient.invalidateQueries(['organizationUnit', variables.ouId]),
    },
  )
}

export const useDeleteLogo = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (ouId: number) => axios.post(`${url}/DeleteLogo`, null, { params: { ouId } }),
    { onSuccess: (_, ouId) => queryClient.invalidateQueries(['organizationUnit', ouId]) },
  )
}

export const exportUrl = `${url}/Export`
